import React from 'react';
import { FiShield, FiUsers, FiFileText } from 'react-icons/fi';
import {
  FormSection,
  SectionTitle,
  SubSectionTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  Select,
  TextArea,
  CheckboxGroup,
  CheckboxLabel,
  ToggleSwitch,
  ToggleInput,
  ToggleLabel,
  InfoBox,
  HelpText
} from '../common/FormStyles';
import { EntrepreneurGuarantees } from '@/types/application-forms';
import { Candidate } from '@/models';

interface EntrepreneurGuaranteesSectionProps {
  data: Partial<Candidate>;
  onChange: (data: Partial<Candidate>) => void;
  readonly?: boolean;
}

export const EntrepreneurGuaranteesSection: React.FC<EntrepreneurGuaranteesSectionProps> = ({
  data,
  onChange,
  readonly = false
}) => {
  const handleChange = (field: string, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleGuaranteeToggle = (type: string, checked: boolean) => {
    const currentGuarantees = data.garanties_proposees || [];
    const newGuarantees = checked
      ? [...currentGuarantees, type as any]
      : currentGuarantees.filter(g => g !== type);
    handleChange('garanties_proposees', newGuarantees);
  };

  return (
    <FormSection>
      <SectionTitle>
        <FiShield />
        Garanties et Références
      </SectionTitle>

      <InfoBox $type="info">
        <p>
          Les garanties et références commerciales nous aident à évaluer votre crédibilité
          et votre intégration dans le tissu économique local.
        </p>
      </InfoBox>

      <SubSectionTitle>
        <FiUsers style={{ fontSize: '18px' }} />
        Associations et Groupements
      </SubSectionTitle>

      <FormGroup>
        <Label>Êtes-vous membre d'une association professionnelle ou d'un groupement ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.membre_association || false}
            onChange={e => handleChange('membre_association', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.membre_association ? 'Oui, je suis membre' : 'Non, pas membre'}
          </ToggleLabel>
        </ToggleSwitch>
        <HelpText>
          Association de commerçants, groupement professionnel, coopérative, etc.
        </HelpText>
      </FormGroup>

      {data.membre_association && (
        <>
          <FormGroup>
            <Label $required>Nom de l'association/groupement</Label>
            <Input
              type="text"
              value={data.nom_association || ''}
              onChange={e => handleChange('nom_association', e.target.value)}
              placeholder="Ex: Association des Commerçants de Yopougon, Groupement des Couturiers..."
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Depuis quelle année ?</Label>
            <Input
              type="number"
              min="1900"
              max={new Date().getFullYear()}
              value={data.annee_adhesion_association || ''}
              onChange={e => handleChange('annee_adhesion_association', parseInt(e.target.value))}
              placeholder="Ex: 2018"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Nom du président de l'association</Label>
            <Input
              type="text"
              value={data.president_association || ''}
              onChange={e => handleChange('president_association', e.target.value)}
              placeholder="Nom complet du président"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Contact du président</Label>
            <Input
              type="tel"
              value={data.contact_president_association || ''}
              onChange={e => handleChange('contact_president_association', e.target.value)}
              placeholder="Ex: +225 07 00 00 00 00"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}

      <FormGroup>
        <Label>Participez-vous à une tontine commerciale/professionnelle ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.participe_tontine_commerciale || false}
            onChange={e => handleChange('participe_tontine_commerciale', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.participe_tontine_commerciale ? 'Oui, je participe' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
        <HelpText>
          Tontine entre commerçants ou professionnels de votre secteur
        </HelpText>
      </FormGroup>

      {data.participe_tontine_commerciale && (
        <FormGrid>
          <FormGroup>
            <Label>Montant de cotisation mensuelle (FCFA)</Label>
            <Input
              type="number"
              min="0"
              value={data.montant_tontine_commerciale || ''}
              onChange={e => handleChange('montant_tontine_commerciale', parseInt(e.target.value))}
              placeholder="Ex: 50000"
              disabled={readonly}
            />
          </FormGroup>

          <FormGroup>
            <Label>Nombre de participants</Label>
            <Input
              type="number"
              min="1"
              value={data.nombre_participants_tontine || ''}
              onChange={e => handleChange('nombre_participants_tontine', parseInt(e.target.value))}
              placeholder="Ex: 10"
              disabled={readonly}
            />
          </FormGroup>
        </FormGrid>
      )}

      <SubSectionTitle>
        <FiFileText style={{ fontSize: '18px' }} />
        Références Commerciales
      </SubSectionTitle>

      <InfoBox $type="warning">
        <p>
          Fournissez au moins deux références commerciales qui peuvent témoigner de votre
          sérieux et de la régularité de votre activité.
        </p>
      </InfoBox>

      <FormGroup>
        <Label $required>Principal fournisseur (référence)</Label>
        <Input
          type="text"
          value={data.reference_fournisseur_principal || ''}
          onChange={e => handleChange('reference_fournisseur_principal', e.target.value)}
          placeholder="Nom du fournisseur qui peut vous recommander"
          disabled={readonly}
        />
      </FormGroup>

      <FormGrid>
        <FormGroup>
          <Label>Contact du fournisseur</Label>
          <Input
            type="tel"
            value={data.contact_fournisseur_principal || ''}
            onChange={e => handleChange('contact_fournisseur_principal', e.target.value)}
            placeholder="+221 X XX XX XX XX"
            disabled={readonly}
          />
        </FormGroup>

        <FormGroup>
          <Label>Durée de la relation (années)</Label>
          <Input
            type="number"
            min="0"
            value={data.duree_relation_fournisseur || ''}
            onChange={e => handleChange('duree_relation_fournisseur', parseInt(e.target.value))}
            placeholder="Ex: 3"
            disabled={readonly}
          />
          <HelpText>Depuis combien d'années travaillez-vous ensemble ?</HelpText>
        </FormGroup>
      </FormGrid>

      <FormGroup>
        <Label>Client important (référence)</Label>
        <Input
          type="text"
          value={data.reference_client_important || ''}
          onChange={e => handleChange('reference_client_important', e.target.value)}
          placeholder="Nom d'un client important qui peut vous recommander"
          disabled={readonly}
        />
        <HelpText>Optionnel - Si vous avez un client régulier qui peut témoigner</HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Contact du client</Label>
        <Input
          type="tel"
          value={data.contact_client_important || ''}
          onChange={e => handleChange('contact_client_important', e.target.value)}
          placeholder="+221 X XX XX XX XX"
          disabled={readonly}
        />
      </FormGroup>

      <FormGroup>
        <Label>Référence de voisinage commercial</Label>
        <Input
          type="text"
          value={data.reference_voisin_commercial || ''}
          onChange={e => handleChange('reference_voisin_commercial', e.target.value)}
          placeholder="Nom d'un commerçant voisin qui peut témoigner de votre activité"
          disabled={readonly}
        />
        <HelpText>Un commerçant établi près de votre local</HelpText>
      </FormGroup>

      <FormGroup>
        <Label>Contact de la référence de voisinage</Label>
        <Input
          type="tel"
          value={data.contact_voisin_commercial || ''}
          onChange={e => handleChange('contact_voisin_commercial', e.target.value)}
          placeholder="+221 X XX XX XX XX"
          disabled={readonly}
        />
      </FormGroup>

      <SubSectionTitle>
        <FiShield style={{ fontSize: '18px' }} />
        Garanties Proposées
      </SubSectionTitle>

      <InfoBox $type="info">
        <p>
          Pour renforcer votre dossier, vous pouvez proposer des garanties supplémentaires.
          Sélectionnez celles que vous êtes prêt à fournir.
        </p>
      </InfoBox>

      <FormGroup>
        <Label $required>Type(s) de garantie(s) que vous proposez</Label>
        <CheckboxGroup>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Caution solidaire d\'un tiers')}
              onChange={e => handleGuaranteeToggle('Caution solidaire d\'un tiers', e.target.checked)}
              disabled={readonly}
            />
            Caution solidaire d'un tiers
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Dépôt de garantie supplémentaire')}
              onChange={e => handleGuaranteeToggle('Dépôt de garantie supplémentaire', e.target.checked)}
              disabled={readonly}
            />
            Dépôt de garantie supplémentaire
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Hypothèque matériel/stock')}
              onChange={e => handleGuaranteeToggle('Hypothèque matériel/stock', e.target.checked)}
              disabled={readonly}
            />
            Hypothèque sur matériel/stock
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Garantie bancaire')}
              onChange={e => handleGuaranteeToggle('Garantie bancaire', e.target.checked)}
              disabled={readonly}
            />
            Garantie bancaire
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Domiciliation des revenus')}
              onChange={e => handleGuaranteeToggle('Domiciliation des revenus', e.target.checked)}
              disabled={readonly}
            />
            Domiciliation de mes revenus commerciaux
          </CheckboxLabel>
          <CheckboxLabel>
            <input
              type="checkbox"
              checked={(data.garanties_proposees || []).includes('Autre')}
              onChange={e => handleGuaranteeToggle('Autre', e.target.checked)}
              disabled={readonly}
            />
            Autre garantie
          </CheckboxLabel>
        </CheckboxGroup>
        <HelpText>Vous pouvez sélectionner plusieurs garanties</HelpText>
      </FormGroup>

      {(data.garanties_proposees || []).includes('Caution solidaire d\'un tiers') && (
        <>
          <FormGroup>
            <Label $required>Nom complet de la caution</Label>
            <Input
              type="text"
              value={data.nom_caution || ''}
              onChange={e => handleChange('nom_caution', e.target.value)}
              placeholder="Nom et prénoms de la personne qui se porte caution"
              disabled={readonly}
            />
          </FormGroup>

          <FormGrid>
            <FormGroup>
              <Label>Lien avec la caution</Label>
              <Select
                value={data.lien_caution || ''}
                onChange={e => handleChange('lien_caution', e.target.value)}
                disabled={readonly}
              >
                <option value="">Sélectionnez...</option>
                <option value="Famille">Membre de la famille</option>
                <option value="Ami proche">Ami proche</option>
                <option value="Partenaire commercial">Partenaire commercial</option>
                <option value="Membre association">Membre de mon association</option>
                <option value="Autre">Autre</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>Contact de la caution</Label>
              <Input
                type="tel"
                value={data.contact_caution || ''}
                onChange={e => handleChange('contact_caution', e.target.value)}
                placeholder="+221 X XX XX XX XX"
                disabled={readonly}
              />
            </FormGroup>
          </FormGrid>

          <FormGroup>
            <Label>Profession de la caution</Label>
            <Input
              type="text"
              value={data.profession_caution || ''}
              onChange={e => handleChange('profession_caution', e.target.value)}
              placeholder="Profession ou activité de la caution"
              disabled={readonly}
            />
          </FormGroup>
        </>
      )}

      {(data.garanties_proposees || []).includes('Dépôt de garantie supplémentaire') && (
        <FormGroup>
          <Label>Montant du dépôt supplémentaire proposé (FCFA)</Label>
          <Input
            type="number"
            min="0"
            value={data.montant_depot_supplementaire || ''}
            onChange={e => handleChange('montant_depot_supplementaire', parseInt(e.target.value))}
            placeholder="Ex: 500000"
            disabled={readonly}
          />
          <HelpText>
            Montant que vous êtes prêt à verser en plus du dépôt de garantie standard
          </HelpText>
        </FormGroup>
      )}

      {(data.garanties_proposees || []).includes('Hypothèque matériel/stock') && (
        <FormGroup>
          <Label>Description du matériel/stock proposé en garantie</Label>
          <TextArea
            value={data.description_materiel_hypotheque || ''}
            onChange={e => handleChange('description_materiel_hypotheque', e.target.value)}
            placeholder="Décrivez le matériel ou le stock que vous proposez en garantie (nature, valeur estimée...)"
            rows={4}
            disabled={readonly}
          />
        </FormGroup>
      )}

      {(data.garanties_proposees || []).includes('Autre') && (
        <FormGroup>
          <Label>Précisez la garantie proposée</Label>
          <TextArea
            value={data.autre_garantie_details || ''}
            onChange={e => handleChange('autre_garantie_details', e.target.value)}
            placeholder="Décrivez la garantie que vous proposez..."
            rows={3}
            disabled={readonly}
          />
        </FormGroup>
      )}

      <FormGroup>
        <Label>Avez-vous des documents justificatifs pour vos garanties ?</Label>
        <ToggleSwitch>
          <ToggleInput
            checked={data.a_documents_garanties || false}
            onChange={e => handleChange('a_documents_garanties', e.target.checked)}
            disabled={readonly}
          />
          <ToggleLabel>
            {data.a_documents_garanties ? 'Oui, j\'ai des documents' : 'Non'}
          </ToggleLabel>
        </ToggleSwitch>
        <HelpText>
          Attestations, certificats de propriété, relevés bancaires, etc.
        </HelpText>
      </FormGroup>

      {data.a_documents_garanties && (
        <FormGroup>
          <Label>Listez les documents que vous pouvez fournir</Label>
          <TextArea
            value={data.liste_documents_garanties || ''}
            onChange={e => handleChange('liste_documents_garanties', e.target.value)}
            placeholder="Ex: Certificat de propriété du fonds de commerce, Attestation du président de l'association, Relevé bancaire..."
            rows={3}
            disabled={readonly}
          />
        </FormGroup>
      )}

      <InfoBox $type="warning">
        <p>
          <strong>Important :</strong> Toutes les références et garanties mentionnées pourront
          faire l'objet d'une vérification. Assurez-vous que les personnes mentionnées sont
          informées et disposées à vous recommander.
        </p>
      </InfoBox>
    </FormSection>
  );
};
